import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn } from "lucide-react";

const GALLERY = [
  {
    src:
      "https://customer-assets.emergentagent.com/job_clami-fillers-chile/artifacts/nr05smg6_Cajas%20CLA_MIGNON%20fondo%20moderno%20blanco.webp",
    caption: "Cajas · Portafolio 201 · 202 · 203",
    alt: "Cajas Clamignon 201, 202 y 203 en composición isométrica",
  },
  {
    src:
      "https://customer-assets.emergentagent.com/job_clami-fillers-chile/artifacts/31402lcn_Producto%20CLA_MIGNON%20sin%20linea%20vertical.png",
    caption: "Presentación clínica · Diseño packaging",
    alt: "Cajas Clamignon 201, 202 y 203 en vista vertical",
  },
  {
    src:
      "https://customer-assets.emergentagent.com/job_clami-fillers-chile/artifacts/99o76l3n_Jeringas%20CLA_MIGNON%20fondo%20moderno%20blanco.webp",
    caption: "Jeringas 1.0 mL · Lidocaína 0.3%",
    alt: "Seis jeringas Clamignon en composición circular",
  },
];

export default function ProductGallery() {
  const [openIdx, setOpenIdx] = useState(null);

  // Close on Escape
  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenIdx(null);
      if (e.key === "ArrowRight")
        setOpenIdx((i) => (i + 1) % GALLERY.length);
      if (e.key === "ArrowLeft")
        setOpenIdx((i) => (i - 1 + GALLERY.length) % GALLERY.length);
    };
    document.addEventListener("keydown", onKey);
    // Prevent body scroll
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIdx]);

  return (
    <section
      data-testid="product-gallery"
      className="relative py-16 md:py-24"
    >
      <div className="max-w-[1160px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {GALLERY.map((img, i) => (
            <motion.figure
              key={img.src}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              data-testid={`gallery-item-${i}`}
              className="flex flex-col"
            >
              <button
                type="button"
                onClick={() => setOpenIdx(i)}
                data-testid={`gallery-open-${i}`}
                aria-label={`Ver ${img.caption} en detalle`}
                className="group relative aspect-square rounded-[24px] overflow-hidden bg-white border border-black/[0.06] shadow-[0_10px_40px_-20px_rgba(90,72,201,0.35)] hover:shadow-[0_16px_50px_-16px_rgba(124,106,245,0.4)] transition-all duration-500"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-contain p-4 md:p-6 transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
                {/* Zoom hint */}
                <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur border border-black/[0.06] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-sm">
                  <ZoomIn className="w-4 h-4 text-[#5A48C9]" strokeWidth={1.8} />
                </span>
              </button>
              <figcaption className="mt-4 text-center text-[11px] md:text-xs font-semibold uppercase tracking-widest text-[#5A48C9]">
                {img.caption}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {openIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="gallery-lightbox"
            onClick={() => setOpenIdx(null)}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-10 bg-[#0B0B14]/85 backdrop-blur-md cursor-zoom-out"
          >
            <button
              type="button"
              aria-label="Cerrar"
              data-testid="lightbox-close"
              onClick={(e) => { e.stopPropagation(); setOpenIdx(null); }}
              className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/95 backdrop-blur border border-white/20 flex items-center justify-center text-[#0B0B14] hover:bg-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <motion.figure
              key={openIdx}
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-[92vw] md:max-w-[85vw] max-h-[85vh] flex flex-col items-center cursor-default"
            >
              <div className="rounded-3xl bg-white p-3 md:p-5 shadow-2xl">
                <img
                  src={GALLERY[openIdx].src}
                  alt={GALLERY[openIdx].alt}
                  className="block max-h-[70vh] w-auto object-contain rounded-2xl"
                />
              </div>
              <figcaption className="mt-4 text-center text-white/90 text-sm md:text-base font-semibold">
                {GALLERY[openIdx].caption}
                <span className="ml-3 text-white/50 text-xs font-mono">
                  {openIdx + 1} / {GALLERY.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
